<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import CriticidadeAPI from 'dashboard/api/fandata/criticidade';

// Criticidade (M8): fila ativa ordenada por risco, com destaque de churn e drill.
//
// T-150b: deixou de ser página e virou a aba "Fila crítica" do Farol. A Tamara pediu o farol
// correlacionado com a criticidade, e mantê-los separados obrigava a olhar duas telas para
// responder a mesma pergunta. Quando `embutido`, o componente abre mão do próprio cabeçalho e
// do próprio respiro: quem os dá é a página que hospeda.
defineProps({
  embutido: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const SENT_STYLE = {
  negativo: 'bg-n-ruby-3 text-n-ruby-11',
  positivo: 'bg-n-teal-3 text-n-teal-11',
  neutro: 'bg-n-amber-3 text-n-amber-11',
};

// Protocolo de criticidade (R8.6) — chip evidente (validação 22/07 / item 17).
const PROTOCOLO_STYLE = {
  tolerancia_zero: 'bg-n-ruby-9 text-white',
  vermelho: 'bg-n-ruby-9 text-white',
  amarelo: 'bg-n-amber-9 text-white',
  verde: 'bg-n-teal-9 text-white',
  cinza: 'bg-n-slate-9 text-white',
};
const PROTOCOLO_LABEL = {
  tolerancia_zero: 'Tolerância Zero',
  vermelho: 'Vermelho',
  amarelo: 'Amarelo',
  verde: 'Verde',
  cinza: 'Cinza',
};

// T-150a: "Positivo/Negativo" viram "Satisfeito/Insatisfeito". O rótulo sai do mesmo lugar do
// Farol para os dois nunca divergirem.
const rotuloSentimento = sentimento =>
  sentimento ? t(`FAROL.SENT.${String(sentimento).toUpperCase()}`) : '';

const items = ref([]);
const churnCount = ref(0);

const scoreStyle = score => {
  if (score >= 80) return 'bg-n-ruby-9 text-white';
  if (score >= 50) return 'bg-n-amber-9 text-white';
  if (score >= 25) return 'bg-n-slate-9 text-white';
  return 'bg-n-alpha-2 text-n-slate-11';
};

const fetchData = async () => {
  try {
    const { data } = await CriticidadeAPI.get();
    items.value = data.items || [];
    churnCount.value = data.churn_count || 0;
  } catch (e) {
    useAlert(t('CRITICIDADE.ERROR'));
  }
};

const openConversation = item => {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: item.id },
  });
};

const hasItems = computed(() => items.value.length > 0);

const { startRefetching } = useLiveRefresh(fetchData, 30000);
onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div
    class="flex flex-col w-full"
    :class="embutido ? '' : 'h-full px-6 py-4 overflow-auto'"
  >
    <!-- Header -->
    <div class="flex items-center gap-2 mb-1">
      <h1
        v-if="!embutido"
        class="text-xl font-semibold text-n-slate-12"
      >
        {{ t('CRITICIDADE.HEADER') }}
      </h1>
      <span
        v-if="!embutido"
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-n-teal-3 text-n-teal-11"
      >
        <span class="size-1.5 rounded-full bg-n-teal-9 animate-pulse" />
        {{ t('CRITICIDADE.LIVE') }}
      </span>
      <span
        v-if="churnCount"
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-n-ruby-3 text-n-ruby-11"
      >
        {{ t('CRITICIDADE.CHURN_COUNT', { n: churnCount }) }}
      </span>
    </div>
    <p class="text-sm text-n-slate-11 mb-4">{{ t('CRITICIDADE.HINT') }}</p>

    <!-- Fila -->
    <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
      <div v-if="!hasItems" class="px-5 py-10 text-center">
        <p class="text-sm text-n-slate-11">{{ t('CRITICIDADE.EMPTY') }}</p>
      </div>

      <ul v-else class="divide-y divide-n-weak">
        <li
          v-for="(item, idx) in items"
          :key="item.id"
          class="flex items-center gap-3 px-4 py-3 hover:bg-n-alpha-1 cursor-pointer"
          @click="openConversation(item)"
        >
          <span class="w-5 text-sm text-n-slate-10 tabular-nums">{{
            idx + 1
          }}</span>
          <span
            class="flex items-center justify-center min-w-9 h-7 px-2 rounded-md text-sm font-bold tabular-nums"
            :class="scoreStyle(item.score)"
          >
            {{ item.score }}
          </span>
          <div class="flex flex-col min-w-0 flex-1 gap-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium text-n-slate-12 truncate">
                {{ item.contact || t('CRITICIDADE.SEM_CONTATO') }}
              </span>
              <span class="text-xs text-n-slate-10">#{{ item.id }}</span>
              <span
                v-if="item.protocolo"
                class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide"
                :class="
                  PROTOCOLO_STYLE[item.protocolo] || 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ PROTOCOLO_LABEL[item.protocolo] || item.protocolo }}
              </span>
              <span
                v-if="item.churn"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-n-ruby-3 text-n-ruby-11"
              >
                {{ t('CRITICIDADE.CHURN') }}
              </span>
              <span
                v-if="item.sentimento"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium capitalize"
                :class="
                  SENT_STYLE[item.sentimento] || 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ rotuloSentimento(item.sentimento) }}
              </span>
              <span
                v-if="item.assunto"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-n-solid-3 text-n-slate-12 capitalize"
              >
                {{ item.assunto }}
              </span>
              <span
                v-if="item.recorrencia > 0"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-n-amber-3 text-n-amber-11"
              >
                {{ t('CRITICIDADE.RECORRENCIA', { n: item.recorrencia }) }}
              </span>
            </div>
            <p v-if="item.resumo" class="text-xs text-n-slate-11 truncate">
              {{ item.resumo }}
            </p>
          </div>
          <span class="i-lucide-chevron-right text-n-slate-10 shrink-0" />
        </li>
      </ul>
    </div>
  </div>
</template>
